import { Injectable } from '@angular/core';
import { BaseResponse, BaseService } from 'src/app/core/services/base.service';
import { HttpClient } from '@angular/common/http';
import { map, catchError, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, throwError } from 'rxjs';

import {
    AddCreditCardResponse,
    BillingFormResponse,
    CreditCard,
    GetCreditCardResponse,
    GetWTNRegisterLinkResponse,
} from './user-types.service';
import { SetupService } from '@core/services/setup';
import { Finance } from './components/finances/finances.interface';

@Injectable({
    providedIn: 'root',
})
export class UserService extends BaseService {
    currentUser: BehaviorSubject<any> = new BehaviorSubject(this.localStorage.getUser());
    subscription$: BehaviorSubject<any> = new BehaviorSubject(null);

    private _sdkMpToken: string;
    get sdkMpToken() {
        return this._sdkMpToken;
    }
    set sdkMpToken(token: string) {
        this._sdkMpToken = token;
    }

    constructor(private http: HttpClient, private setupService: SetupService) {
        super();
    }

    getFiliations() {
        let body = this.setBody();
        const affiliationType = localStorage.getItem('tnt.affiliationType');
        if (affiliationType) body.set('affiliationType', affiliationType);
        const response = this.http
            .post(this.urlService + this.routeAdress.route_affiliation, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));
        return response;
    }

    getProfile() {
        const currentUser = this.localStorage.getUser();
        const userId = currentUser.id_site;
        //console.log('CURRENT USER DENTRO DO PROFILE', currentUser);
        const body = this.setBody();
        body.set('profileId', userId);

        let response = this.http
            .post(this.urlService + this.routeAdress.route_user_profile, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));
        return response;
    }

    getCredit() {
        const body = this.setBody();

        let response = this.http
            .post(this.urlService + this.routeAdress.route_credit, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));
        return response;
    }

    getTournamentSituationFinances() {
        const body = this.setBody();
        return this.http
            .post(this.urlService + this.routeAdress.route_tournament_financial_situation, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));
    }

    getEventsFinances() {
        const body = this.setBody();
        return this.http
            .post(this.urlService + this.routeAdress.route_events_finance, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));
    }

    async mergePaymentMethods(response: any) {
        if (!response.registers) return response;
        const registers = await Promise.all(
            response?.registers?.map(async (item) => {
                if (!item.id_fatura && !item.id_sub_fatura) return item;

                const billingFormBody = this.setBody();
                billingFormBody.set('billingId', item.id_fatura);
                billingFormBody.set('billingSubId', item.id_sub_fatura);

                try {
                    const response = await this.http
                        .post<BillingFormResponse>(
                            this.urlService + this.routeAdress.route_payments_billing_form,
                            billingFormBody.toString()
                        )
                        .toPromise();
                    if (!response.registers && !response.registers?.datas?.registers) return item;
                    const paymentBtns = response.registers?.datas?.registers;
                    // console.log(response.registers);
                    return {
                        ...item,
                        paymentBtns,
                    };
                } catch (error) {
                    console.error(error);
                }
            })
        );

        return {
            ...response,
            registers,
        };
    }

    getAffiliationFinances(): Observable<any> {
        const body = this.setBody();

        return this.http.post(this.urlService + this.routeAdress.route_affiliation_financial, body.toString()).pipe(
            tap((response: any) => {
                if (response.registers?.length) {
                    const finance = response.registers[0];
                    this.sdkMpToken = finance.setButton.param.mpago_jsdk;
                }
            }),
            catchError(this.serviceError)
        );
    }

    getFinanceByID(billingID: string) {
        const body = this.setBody();
        body.set('billingId', billingID);

        return this.http
            .post<BaseResponse<Finance>>(this.urlService + this.routeAdress.route_payments_get_billing, body.toString())
            .pipe(
                tap((response) => {
                    this.sdkMpToken = response.registers?.setButton?.param?.mpago_jsdk;
                    return response;
                })
            );
    }

    getCertificates() {
        const body = this.setBody();

        let response = this.http
            .post(this.urlService + this.routeAdress.route_certificates, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));
        return response;
    }

    downloadCertificate(payload: { [key: string]: any }) {
        const body = this.setBody();

        Object.keys(payload).forEach((key) => body.set(key, payload[key]));

        let response = this.http.post(this.urlService + this.routeAdress.route_download_certificate, body.toString(), {
            responseType: 'arraybuffer',
            headers: {
                accept: 'application/pdf',
            },
        });
        return response;
    }

    getClubs(keyword?) {
        const body = this.setBody();
        body.set('search', keyword);

        let response = this.http
            .post(this.urlService + this.routeAdress.route_site_club, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError))
            .pipe(map((response) => response.registers));
        return response;
    }

    updateFinancial(billingId, financial) {
        const body = this.setBody();
        body.set('billingId', billingId);
        body.set('financial', JSON.stringify(financial));

        let response = this.http
            .post(this.urlService + this.routeAdress.route_payments_update_financial, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));
        return response;
    }

    openBoleto(billingId, billingSubId) {
        const body = this.setBody();
        body.set('billingId', billingId);
        body.set('billingSubId', billingSubId);

        let response = this.http
            .post(this.urlService + this.routeAdress.route_payments_billing_open, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));
        return response;
    }

    copyBarCode(billingId, billingSubId) {
        const body = this.setBody();
        body.set('billingId', billingId);
        body.set('billingSubId', billingSubId);

        let response = this.http
            .post(this.urlService + this.routeAdress.route_payments_bar_code, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));
        return response;
    }

    updateProfile(formValues, tab) {
        const userId = this.currentUser.getValue().id_site;
        const body = this.setBody();

        body.set('profileId', userId);

        const birth_date = formValues.general.birth_date?.toLocaleDateString('pt-BR');
        const general = {
            ...formValues.general,
            cell_phone_ddi: formValues.general.ddi,
            cell_phone_ddd: formValues.general.ddd,
            cell_phone_number: formValues.general.cellphone,
            birth_date,
        };

        switch (tab) {
            case 'general':
                body.set('general', JSON.stringify(general));
                break;
            case 'locale':
                body.set('locale', JSON.stringify(formValues.locale));
                break;
            case 'optional':
                body.set('optional', JSON.stringify(formValues.optional));
                break;
            case 'financial':
                body.set('financial', JSON.stringify(formValues.financial));
                break;
        }

        let response = this.http
            .post(this.urlService + this.routeAdress.route_user_update_profile, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));
        return response;
    }

    changeAvatar(imageBase64) {
        const body = this.setBody();
        body.set('avatar', imageBase64);

        let response = this.http
            .post(this.urlService + this.routeAdress.route_user_change_avatar, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));
        return response;
    }

    resetClub(clubType) {
        const body = this.setBody();
        body.set('clubType', clubType);

        let response = this.http
            .post(this.urlService + this.routeAdress.route_site_reset_club, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));
        return response;
    }

    getLicenses() {
        const body = this.setBody();
        const affiliationType = localStorage.getItem('tnt.affiliationType');
        if (affiliationType) body.set('affiliationType', affiliationType);
        let response = this.http
            .post(this.urlService + this.routeAdress.route_affiliation_list, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));
        return response;
    }

    confirmLicense(keyId) {
        const body = this.setBody();
        body.set('keyId', JSON.stringify(keyId));
        const affiliationType = localStorage.getItem('tnt.affiliationType');
        if (affiliationType) body.set('affiliationType', affiliationType);
        let response = this.http
            .post(this.urlService + this.routeAdress.route_affiliation_confirm, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));
        return response;
    }

    addLicense(keyId) {
        const body = this.setBody();
        body.set('keyId', JSON.stringify(keyId));
        const affiliationType = localStorage.getItem('tnt.affiliationType');
        if (affiliationType) body.set('affiliationType', affiliationType);
        let response = this.http
            .post(this.urlService + this.routeAdress.route_affiliation_add, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));
        return response;
    }

    cancelLicense(affiliationId) {
        const body = this.setBody();
        body.set('affiliationId', affiliationId);
        const affiliationType = localStorage.getItem('tnt.affiliationType');
        if (affiliationType) body.set('affiliationType', affiliationType);
        let response = this.http
            .post(this.urlService + this.routeAdress.route_affiliation_cancel, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));
        return response;
    }

    initPayment(billingId, billingSubId?) {
        const body = this.setBody();
        body.set('billingId', billingId);
        if (billingSubId) {
            body.set('billingSubId', billingSubId);
        }
        let response = this.http
            .post(this.urlService + this.routeAdress.route_payments_get, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));
        return response;
    }

    saveUserToLocalStorage(user) {
        this.localStorage.saveUser(user);
        this.currentUser.next(user);
    }

    getMyGames(filters) {
        const body = this.setBody();
        if (filters) {
            Object.keys(filters).forEach((filter) => {
                if (filters[filter]) {
                    if (filters[filter] instanceof Array) {
                        body.set(filter, `[${filters[filter].join(',')}]`);
                    } else {
                        body.set(filter, filters[filter]);
                    }
                }
            });
        }
        let response = this.http
            .post(this.urlService + this.routeAdress.route_tournament_mygames, body.toString())
            .pipe(
                map(this.extractData),
                map((response) => {
                    /* AJUSTA ESTRUTURA INPUT */
                    const { filterSelected, filters, list } = response.registers;
                    let games = list;
                    if (list) {
                        games = list.map((game) => {
                            const participantGroup = Object.entries(game.participantes).reduce((acc, item: any) => {
                                // cada key é um lado. ex: [ 1: [], 2: [] ].. 1 e 2 são 'lados'
                                // o valor dessa key é o array de participantes
                                const [lado, arrParticipants] = item;

                                const detailedResults = game.resultado_detalhado,
                                    results = game.resultado;

                                let participantResults;
                                if (detailedResults) {
                                    const [lado1Results, lado2Results] = Object.values(detailedResults);
                                    participantResults =
                                        lado === '1'
                                            ? Object.values(lado1Results).flat()
                                            : Object.values(lado2Results).flat();
                                } else {
                                    participantResults = Array.from(results[lado]);
                                }

                                // precisamos do lado para marcar quem é o campeão
                                // pois api retorna `lado_vencedor: 2`
                                // vamos salvar no objeto do participante

                                const newObjStructure = {
                                    duplas: Boolean(arrParticipants.length > 1),
                                    lado: Number(lado),
                                    participants: arrParticipants,
                                    result: participantResults, //adiciona também resultado ao objeto do participante
                                };

                                acc.push(newObjStructure);
                                return acc;
                            }, []);
                            const newObj = {
                                participantGroup,
                                ...game,
                            };

                            return newObj;
                        });
                    }

                    return {
                        list: games,
                        filters,
                        filterSelected,
                    };
                }),
                catchError(this.serviceError)
            );
        return response;
    }

    getAffiliationsTypes(): Observable<any> {
        return this.http.post(this.urlService + this.routeAdress.route_affiliation_types, this.setBody().toString());
    }

    getAffiliationProfile(): Observable<any> {
        const body = this.setBody();
        const affiliationType = localStorage.getItem('tnt.affiliationType');
        if (affiliationType) body.set('affiliationType', affiliationType);
        return this.http.post(this.urlService + this.routeAdress.route_affiliation_get_profile, body.toString()).pipe(
            map((response: any) => {
                if (response.registers.docs) {
                    response.registers.docs = response.registers.docs.document;
                }
                return response;
            })
        );
    }

    getDocumentProfile(): Observable<any> {
        const body = this.setBody();
        return this.http.post(
            this.urlService + this.routeAdress.route_affiliation_get_document_profile,
            body.toString()
        );
    }

    updateAffiliationProfile(data): Observable<any> {
        const body = this.setBody();
        body.set('general', JSON.stringify(data.general));
        body.set('game', JSON.stringify(data.game));
        body.set('docs', JSON.stringify(data.docs));
        const affiliationType = localStorage.getItem('tnt.affiliationType');
        if (affiliationType) body.set('affiliationType', affiliationType);
        return this.http.post(this.urlService + this.routeAdress.route_affiliation_set_profile, body.toString()).pipe(
            map((response: any) => {
                if (response.registers?.docs) {
                    response.registers.docs = response.registers.docs.document;
                }
                if (response.registers?.general?.tshirt_size) {
                    response.registers.general.tshirt_size.value = Number(response.registers.general.tshirt_size.value);
                }
                return response;
            })
        );
    }

    updateAffiliationDocs(data, fileStatus): Observable<any> {
        const { docs, ...files } = data;
        const body = new FormData();
        body.append('token', this.localStorage.getToken());
        body.append('host', this.localStorage.getHost());
        body.append('system', this.identifySystem);
        body.append('language', this.localStorage.getUserLangs().selected);
        body.append('docs', JSON.stringify(docs));
        body.append('file_status', JSON.stringify(fileStatus));
        Object.entries(files).forEach(([key, value]: any) => {
            value.forEach((file) => {
                if (!file) return;
                if (typeof file === 'string') {
                    body.append(`${key}[]`, file);
                } else {
                    const nameArr = file.name?.split('.') || [];
                    const first = nameArr.at(0);
                    const last = nameArr.at(-1);
                    const fileName = `${first}.${last}`;
                    console.log(fileName);

                    body.append(`${key}[]`, file, fileName);
                }
            });
        });

        return this.http.post(this.urlService + this.routeAdress.route_affiliation_set_document_profile, body);
    }

    deleteAffiliationDoc(docType, docValue, docSide, docUrl): Observable<any> {
        const body = this.setBody();
        body.set('tipo_documento', docType);
        body.set('nr_documento', docValue);
        body.set('nr_lado', docSide);
        body.set('path_file', docUrl);

        return this.http.post(
            this.urlService + this.routeAdress.route_affiliation_del_document_profile,
            body.toString()
        );
    }

    initPixPayment(billingId, billingSubId): Observable<any> {
        if (!billingId || !billingSubId) return throwError('sem dados billingId e billingSubId para pagamento');
        const body = this.setBody();
        body.set('billingId', billingId);
        body.set('billingSubId', billingSubId);

        // return of(pixResponse);
        return this.http
            .post(this.urlService + this.routeAdress.route_payments_pix, body.toString())
            .pipe((map(this.extractData), catchError(this.serviceError)));
    }

    listenToPayment(billingId, billingSubId): Observable<any> {
        if (!billingId || !billingSubId) return throwError('sem dados billingId e billingSubId para pagamento');
        const body = this.setBody();
        body.set('billingId', billingId);
        body.set('billingSubId', billingSubId);
        return this.http
            .post(this.urlService + this.routeAdress.route_payments_confirm, body.toString())
            .pipe((map(this.extractData), catchError(this.serviceError)));
    }

    payCreditCard(billingId: string, creditCard: CreditCard): Observable<any> {
        const baseURL = this.setupService.urlAppTi;
        const endpoint = '/payment/paymentFromCieloTennistool';

        const body = this.setBody();
        body.set('billing_id', billingId);
        Object.keys(creditCard).forEach((key) => {
            body.set(key, creditCard[key]);
        });

        return this.http
            .post(`${baseURL}${endpoint}`, body.toString())
            .pipe((map(this.extractData), catchError(this.serviceError)));
    }

    getCreditCards() {
        const body = this.setBody();
        const baseURL = this.setupService.urlAppTi;

        return this.http
            .post<GetCreditCardResponse>(baseURL + this.routeAdress.route_credit_card_get, body.toString())
            .pipe(
                tap((response) => {
                    this.sdkMpToken = response.registers.mpago_jsdk;
                    return response;
                }),
                catchError(this.serviceError)
            );
    }

    addCreditCard(creditCard, token_mp) {
        const body = this.setBody();
        body.set('token_mp', token_mp);
        body.set('id_site', this.currentUser.getValue().id_site);
        Object.entries(creditCard).forEach(([k, v]) => body.set(k, v));
        const baseURL = this.setupService.urlAppTi;

        return this.http
            .post<AddCreditCardResponse>(baseURL + this.routeAdress.route_credit_card_add, body.toString())
            .pipe((map(this.extractData), catchError(this.serviceError)));
    }

    updateCreditCard(cardId) {
        const body = this.setBody();
        body.set('id_cartao', cardId);
        body.set('id_site', this.currentUser.getValue().id_site);

        const baseURL = this.setupService.urlAppTi;

        return this.http
            .post<AddCreditCardResponse>(baseURL + this.routeAdress.route_credit_card_update, body.toString())
            .pipe((map(this.extractData), catchError(this.serviceError)));
    }

    getWtnLink() {
        if (this.currentUser.getValue() == null) {
            return null;
        }

        const body = this.setBody();

        body.set('id_site', this.currentUser.getValue().id_entity);
        body.set('id_usuario', this.currentUser.getValue().id_site);

        return this.http
            .post<GetWTNRegisterLinkResponse>(
                this.urlService + this.routeAdress.route_wtn_register_link,
                body.toString()
            )
            .pipe((map(this.extractData), catchError(this.serviceError)));
    }
}
